import { inject, Injectable } from '@angular/core';
import { UIRepository } from '../state/ui/ui.repository';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { GameService } from '@app/game/game.service';
import { Modal, ModalConfig, ModalType, NonPersistedModalName, PersistedModalName } from '@shared/models/modal.model';
import { catchError, concatMap, filter, switchMap, tap, throttle } from 'rxjs/operators';
import { User } from '../models/user.model';
import { VersionUpdate } from '../models/version.model';
import { EventManager } from '../utils/event-manager';
import { LoggerService } from './logger.service';
import { RealtimeService } from './realtime.service';
import { LobbyService } from './lobby.service';
import { ModalComponentManager } from '../utils/modal-component-manager';
import { ShopOffer } from '../models/offer.model';
import { ShopService } from './shop.service';
import { AppService } from '@app/app.service';
import { NativeService, PurchaseProductInfo } from './native.service';
import { Dictionary } from '../utils/dictionary-creator';
import { ToastrUtils } from '../utils/toastrs';
import { EMPTY, lastValueFrom, merge, Subject, timer } from 'rxjs';
import { GlobalDataRepository } from '../state/global-data/global-data.repository';
import { GamesRepository } from '../state/games/games.repository';
import { NotificationsRepository } from '../state/notifications/notifications.repository';
import { OffersRepository } from '../state/offer/offer.repository';
import { UserRepository } from '../state/user/user.repository';
import { VersionRepository } from '../state/version/version.repository';
import { RECALL_MARGIN_TIME } from '@app/shared/utils/constants';
import { TesterRepository } from '../state/tester/tester.repository';
import { TagsService } from './tags.service';

@Injectable({
  providedIn: 'root',
})
export class ModalManagerService {
  private pendingModals: ModalConfig[] = [];
  private openedNonPersistedModals: Map<string, Modal> = new Map<string, Modal>();
  private openedPersistedModals: Map<string, ModalConfig> = new Map<string, ModalConfig>();
  private isModalOpen = false;
  private stopQueue = false;
  private persistedAboveView = false;
  private updateFEAfterGameFinish = false;
  private isProcessingGameFinish = false;
  private readonly uiRepo = inject(UIRepository);
  private readonly tagsService = inject(TagsService);

  private get modalsQueue(): ModalConfig[] {
    return this.notificationsRepo.getModals();
  }

  private readonly refreshUserNotifications$ = new Subject<{ delayMs: number; forceRefresh: boolean }>();

  public get nonPersistedModalsQueue(): Set<string> {
    return new Set(this.openedNonPersistedModals.keys());
  }

  get user(): User {
    return this.userRepo.getUser();
  }

  public readonly logger = inject(LoggerService);
  private readonly eventManager = inject(EventManager);
  private readonly notificationsRepo = inject(NotificationsRepository);
  private readonly gameService = inject(GameService);
  private readonly userRepo = inject(UserRepository);
  private readonly offersRepo = inject(OffersRepository);
  private readonly shopService = inject(ShopService);
  private readonly signalR = inject(RealtimeService);
  private readonly nativeService = inject(NativeService);
  private readonly modalComponentManager = inject(ModalComponentManager);
  private readonly router = inject(Router);
  private readonly versionRepo = inject(VersionRepository);
  private readonly lobbyService = inject(LobbyService);
  private readonly appService = inject(AppService);
  private readonly globalDataRepo = inject(GlobalDataRepository);
  private readonly toastrUtils = inject(ToastrUtils);
  private readonly gamesRepo = inject(GamesRepository);
  private readonly testerRepo = inject(TesterRepository);
  private readonly matDialog = inject(MatDialog);

  public initAllSubscriptions() {
    this.eventManager.checkModalsFromLobby$.subscribe(({ delayMs, forceRefresh }) => {
      this.requestUserNotifications(delayMs, forceRefresh);
    });

    this.notificationsRepo.modals$.subscribe(() => {
      this.checkQueue();
    });

    this.gameService.gameFinished$.subscribe(() => {
      this.isProcessingGameFinish = true;
      if (this.updateFEAfterGameFinish) {
        this.eventManager.closeAllModals();
        this.openUpdateVersionModal();
        this.updateFEAfterGameFinish = false;
      } else {
        if (this.pendingModals.length > 0) {
          this.addPendingModals();
        } else {
          this.stopQueue = false;

          this.checkQueue();
        }
      }

      this.isProcessingGameFinish = false;
    });

    this.eventManager.openModal$.subscribe((modal: Modal) => {
      this.openNonPersisted(modal);
    });

    this.eventManager.closeModal$.subscribe((modal: Modal) => {
      if (modal.modalType === ModalType.View) {
        this.closeNonPersisted(modal);
      } else if (
        modal.modalType === ModalType.Persisted ||
        modal.modalType === ModalType.Info ||
        modal.modalType === ModalType.Offer
      ) {
        this.closePersisted(modal);
      } else {
        this.logger.log('Cant close modal => ' + JSON.stringify(modal));
      }
    });

    this.eventManager.closeAllModals$.subscribe(() => {
      this.openedNonPersistedModals.clear();
      this.openedPersistedModals.clear();
      this.isModalOpen = false;

      this.matDialog.closeAll();
    });

    this.signalR.on('UserNotifications').subscribe((modals: ModalConfig[]) => {
      const includesBuySuccessModal = modals.find((modal) => modal.modalType === PersistedModalName.BuySuccess);

      if (includesBuySuccessModal) {
        this.manageBuySuccessModal();
      } else if (this.gameService.isGameRunning() || this.isProcessingGameFinish) {
        this.pendingModals.push(...this.createPersistedModals(modals));
      } else {
        const BUY_SUCCESS_GRACE_TIME = 4000;
        setTimeout(() => {
          this.persistedAboveView = false;
          this.notificationsRepo.addModals(this.createPersistedModals(modals));
        }, BUY_SUCCESS_GRACE_TIME);
      }
    });

    this.signalR.on('NewVersion').subscribe((updateVersion: VersionUpdate) => {
      // Detect if user moves anywhere from gameView - Flux 1
      this.router.events.pipe(filter((e: NavigationEnd) => e instanceof NavigationEnd)).subscribe((route) => {
        if (route.url.includes('games')) {
          this.appService.reload();
        }
      });
      this.updateVersion(updateVersion);
    });

    this.globalDataRepo.promoCode$.subscribe((promoCode: string) => {
      if (promoCode && this.userRepo.getUser()?.id) {
        this.requestUserNotifications();
      }
    });

    const forcedRefresh$ = this.refreshUserNotifications$.pipe(filter(({ forceRefresh }) => forceRefresh));
    const throttledRefresh$ = this.refreshUserNotifications$.pipe(
      filter(({ forceRefresh }) => !forceRefresh),
      throttle(() => timer(RECALL_MARGIN_TIME), { leading: true, trailing: false })
    );

    merge(forcedRefresh$, throttledRefresh$)
      .pipe(
        concatMap(({ delayMs }) =>
          timer(delayMs).pipe(
            switchMap(() => {
              const promoCode = this.globalDataRepo.getAndDeletePromoCode();
              return this.lobbyService.getUserNotifications(promoCode);
            }),
            tap((userNotifications) => {
              this.pendingModals = [];
              let notifications = userNotifications;

              if (this.user.isTester) {
                notifications = this.filterBlockedModals(userNotifications);
              }

              this.notificationsRepo.setModals(this.createPersistedModals(notifications));
            }),
            catchError((err) => {
              this.logger.error('[GetUserNotifications] error', err);
              this.notificationsRepo.setModals([]);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();
  }

  requestUserNotifications(delayMs?: number, forceRefresh = false): void {
    this.refreshUserNotifications$.next({ delayMs: delayMs ?? 0, forceRefresh });
  }

  private enqueue(modal: ModalConfig) {
    this.notificationsRepo.addModal(modal);
  }

  private dequeue(modal: ModalConfig) {
    this.notificationsRepo.removeModal(modal.id);
  }

  private checkOpenedNonPersistedModalList(): boolean {
    return !this.persistedAboveView ? this.openedNonPersistedModals.size > 0 : false;
  }

  public checkQueue() {
    if (this.modalsQueue.length <= 0 || this.checkOpenedNonPersistedModalList() || this.isModalOpen || this.stopQueue) {
      this.stopQueue = false;
      return;
    }

    const sortedQueue = this.modalsQueue.sort((a, b) => b.priority - a.priority);

    let isEnqueued = false;
    sortedQueue.find((modalQueued) => {
      if (modalQueued.id == sortedQueue[0].id) {
        isEnqueued = true;
      }
    });

    if (!isEnqueued) {
      this.enqueue(this.modalsQueue[0]);
    }

    if (sortedQueue[0].modalType === PersistedModalName.OfferPlaceholder) {
      this.manageOfferModal(sortedQueue[0]);
    } else {
      this.openPersisted(sortedQueue[0]);
    }
  }

  private openPersisted(modal: ModalConfig) {
    const angularComponent = this.modalComponentManager.findComponentType(modal);
    const isExpired = modal.expirationDate ? new Date(modal.expirationDate) < new Date() : false;

    if (!angularComponent || this.matDialog.openDialogs.find((dialog) => dialog.id === modal.id) || isExpired) {
      this.dequeue(modal);
      return;
    }

    this.isModalOpen = true;
    this.openedPersistedModals.set(modal.id, modal);
    this.matDialog.open(angularComponent, {
      data: modal,
      id: modal.id,
      backdropClass: 'persisted-dialog-backdrop',
      panelClass: 'persisted-dialog-overlay',
      disableClose: true,
      autoFocus: false,
    });
  }

  private closePersisted(modal: Modal) {
    if (modal.redirect) {
      this.stopQueue = true;
    }

    this.openedPersistedModals.delete(modal.id);
    this.matDialog.getDialogById(modal.id)?.close();
    this.isModalOpen = false;

    if (modal.modalType !== ModalType.Info && modal.modalType !== ModalType.Offer && modal.deleteModal) {
      this.resolveNotification(modal).catch((e) => this.logger.error('Unexpected error', e));
    }

    if (modal.deleteModal) {
      this.dequeue(modal.data);
    }
  }

  private openNonPersisted(modal: Modal): void {
    if (!this.openedNonPersistedModals.has(modal.id)) {
      this.matDialog.open(this.modalComponentManager.setNonPersisted(modal.id as NonPersistedModalName), {
        id: modal.id,
        data: modal,
        backdropClass: this.manageBackdropZIndex(modal.id as NonPersistedModalName),
        panelClass: [modal.panelClass, this.managePanelZIndex(modal.id as NonPersistedModalName)],
        disableClose: true,
        autoFocus: false,
      });

      this.openedNonPersistedModals.set(modal.id, modal);
    }
  }

  private closeNonPersisted(modal: Modal): void {
    this.openedNonPersistedModals.delete(modal.id);
    this.matDialog.getDialogById(modal.id)?.close(modal?.data?.message ?? '');

    if (!modal.redirect) {
      this.checkQueue();
    }
  }

  private createPersistedModals(modalsConfig: ModalConfig[]): ModalConfig[] {
    return modalsConfig.map(
      (modalConfig) =>
        new ModalConfig(
          modalConfig.id,
          modalConfig.userId,
          modalConfig.modalType,
          this.modalComponentManager.findComponentType(modalConfig),
          modalConfig.expirationDate,
          modalConfig.properties,
          modalConfig.reward,
          modalConfig.unlockGames,
          this.modalComponentManager.getPriority(modalConfig.modalType as PersistedModalName),
          modalConfig.fullScreen,
          modalConfig.prestigeReward
        )
    );
  }

  private updateVersion(updateVersion: VersionUpdate) {
    // Random 0 to 5 min = 360000 - 0
    if (updateVersion.version !== this.versionRepo.getFrontendVersion() && !this.userRepo.getUser().isTester) {
      const modalDispatcherInterval = Math.floor(Math.random() * 360000 + 1) + updateVersion.interval;

      setTimeout(() => {
        this.eventManager.closeAllModals();

        if (this.gameService.isGameRunning()) {
          // User in GameView we wait until GameFinish then dispatch UpdateVersionModalComponent - Flux 2
          this.updateFEAfterGameFinish = true;
        } else {
          // User not in GameView dispatch UpdateVersionModalComponent - Flux 3
          this.updateFEAfterGameFinish = false;
          this.openUpdateVersionModal();
        }
      }, modalDispatcherInterval);
    }
  }

  public openUpdateVersionModal(): void {
    if (!this.matDialog.getDialogById(NonPersistedModalName.UpdateVersionModalComponent)) {
      this.eventManager.openModal(new Modal(NonPersistedModalName.UpdateVersionModalComponent, ModalType.View));
    }
  }

  private async resolveNotification(modal: Modal) {
    this.handleUnlockGames(modal);

    await lastValueFrom(this.lobbyService.deleteUserNotification(modal.id));
  }

  private manageBuySuccessModal(): void {
    this.persistedAboveView = true;

    setTimeout(() => {
      this.eventManager.checkModalsFromLobby(0, true);
      this.eventManager.closeModal(new Modal(NonPersistedModalName.ShopModalComponent, ModalType.View, {}, '', true));
    }, 3000);
  }

  private manageOfferModal(modalConfig: ModalConfig): void {
    this.shopService.getShopOffers(this.userRepo.getCulture()).subscribe((offers: ShopOffer[]) => {
      const currentElfOffers = this.offersRepo.getOffers();
      const viewedOffers =
        !!offers?.length &&
        offers.every((offer) => currentElfOffers.some((elfOffer) => offer.offerId === elfOffer.offerId));

      this.shopService.setOffersToElf(offers);

      if (offers?.length && !viewedOffers) {
        this.openOfferModal(modalConfig, offers);
      }

      this.notificationsRepo.removeModal(modalConfig.id);
    });
  }

  private async openOfferModal(modalConfig: ModalConfig, offers: ShopOffer[]) {
    if (this.nativeService.isNative()) {
      await this.fetchNativeOfferInfo(offers);
    }

    this.openPersisted(this.createOfferConfig(modalConfig));
  }

  private createOfferConfig(modalConfig: ModalConfig): ModalConfig {
    return new ModalConfig(
      NonPersistedModalName.OfferModalComponent,
      modalConfig.userId,
      modalConfig.modalType,
      this.modalComponentManager.findComponentType(modalConfig),
      modalConfig.expirationDate,
      modalConfig.properties,
      modalConfig.reward,
      modalConfig.unlockGames,
      this.modalComponentManager.getPriority(modalConfig.modalType as PersistedModalName),
      modalConfig.fullScreen,
      modalConfig.prestigeReward
    );
  }

  private async fetchNativeOfferInfo(offers: ShopOffer[]) {
    try {
      const skus = [];
      offers.forEach((offer) => skus.push(offer.sku, offer.priceSku));

      const nativeOfferInfoDict = await this.nativeService.findPurchaseProductInfo(skus);

      offers.forEach((offer) => {
        this.updateOfferWithNativeStoreInfo(offer, nativeOfferInfoDict);
      });
    } catch (error) {
      this.logger.error('Error fetching native offer info:', error);
    }
  }

  private updateOfferWithNativeStoreInfo(activeOffer: ShopOffer, nativeOfferInfoDict: Dictionary<PurchaseProductInfo>) {
    if (activeOffer.sku in nativeOfferInfoDict) {
      activeOffer.originalPrice.formattedPrice = nativeOfferInfoDict[activeOffer.sku].formattedPrice;
    }

    if (activeOffer.priceSku in nativeOfferInfoDict) {
      activeOffer.finalPrice.formattedPrice = nativeOfferInfoDict[activeOffer.priceSku].formattedPrice;
    }
  }

  private addPendingModals(): void {
    this.notificationsRepo.addModals(this.createPersistedModals(this.pendingModals));

    this.pendingModals = [];
  }

  private handleUnlockGames(modal: Modal): void {
    const unlockGames: string[] = modal?.data?.unlockGames;

    if (unlockGames?.length) {
      unlockGames.forEach((gameId) => {
        const game = this.gamesRepo.getGameById(gameId);

        if (!game) return;

        if (game?.isHidden || !game?.isUnlocked) {
          this.gamesRepo.updateGameById(gameId, { isHidden: false, isUnlocked: true });
          this.uiRepo.addRecentlyUnlockedGame(gameId);
          this.tagsService.removeTagToGame(gameId, this.tagsService.getLockedTag());
          this.toastrUtils.throwUnlockGameToastr(game);
        }
      });
    }
  }

  private manageBackdropZIndex(modalId: NonPersistedModalName): string {
    switch (modalId) {
      case NonPersistedModalName.RateModalComponent:
        return 'persisted-dialog-backdrop';
      case NonPersistedModalName.TesterModalComponent:
        return 'tester-dialog-backdrop';
      default:
        return 'nonPersisted-dialog-backdrop';
    }
  }

  private managePanelZIndex(modalId: NonPersistedModalName): string {
    switch (modalId) {
      case NonPersistedModalName.RateModalComponent:
        return 'persisted-dialog-overlay';
      case NonPersistedModalName.TesterModalComponent:
        return 'tester-dialog-overlay';
      default:
        return 'nonPersisted-dialog-overlay';
    }
  }

  private filterBlockedModals(modals: ModalConfig[]): ModalConfig[] {
    const blockedSet = new Set(this.testerRepo.getBlockedModals());

    return modals.filter((modal) => !blockedSet.has(modal.modalType));
  }
}
